import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { style, targetStyle } from './image-hotspot.style.js'
import { BrandTarget, ProductTarget, TagTarget, TreeTarget } from './typing/target-types.js'
import { ImageHotspotTargetClickedEvent } from './components/popup/popup.js'
import './components/popup/popup.js'
import './components/slide-in/slide-in.js'
import { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { IMAGE_HOTSPOT_STORE, ImageHotspotStore } from './image-hotspot-store.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


@customElement('csc-image-hotspot')
export class CSCImageHotspot extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, targetStyle, style]

  @property({ type: String, attribute: 'image-source' })
  imageSource: string = ''

  @property({ type: String, attribute: 'image-alt' })
  imageAlt: string = ''

  @property({ type: Array, attribute: 'brand-targets' })
  brandTargets: Array<BrandTarget> = []

  @property({ type: Array, attribute: 'product-targets' })
  productTargets: Array<ProductTarget> = []

  @property({ type: Array, attribute: 'tag-targets' })
  tagTargets: Array<TagTarget> = []

  @property({ type: Array, attribute: 'tree-targets' })
  treeTargets: Array<TreeTarget> = []

  @property({ type: Boolean, attribute: 'display-rating' })
  displayRating: boolean = false

  @property({ type: Boolean, attribute: 'display-stock' })
  displayStock: boolean = false

  @property({ type: Boolean, attribute: 'display-prices' })
  displayPrices: boolean = false

  @property({ type: String, attribute: 'locale' })
  locale: string = 'en-US'

  @inject(IMAGE_HOTSPOT_STORE)
  imageHotspotStore!: ImageHotspotStore

  @state()
  activeTarget: string | null = null

  @query('csc-image-hotspot-slide-in')
  productSlideIn!: CSESlideIn

  override connectedCallback() {
    super.connectedCallback()
    document.addEventListener('click', this.closeAllPopups)
    window.addEventListener('scroll', this.closeAllPopups, { passive: true })
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this.closeAllPopups)
    window.removeEventListener('scroll', this.closeAllPopups)
  }

  private closeAllPopups = () => {
    this.activeTarget = null
  }

  private targetClicked = (targetId: string) => {
    this.activeTarget = targetId
  }

  private productTargetClicked = (productEndpoint: string) => {
    this.imageHotspotStore.setOrFetchProduct(productEndpoint)
    this.activeTarget = 'product'
    this.productSlideIn.open()
  }

  override render() {
    return html`
      <div class="csc-image-hotspot">
        <cse-image 
          source=${this.imageSource} 
          alt=${this.imageAlt}
        ></cse-image>

        ${this.brandTargets.map((target, index) => html`
          <csc-image-hotspot-popup
            popup-id="${`brand_${index}`}"
            ?active="${`brand_${index}` === this.activeTarget}"
            @image-hotspot-target-clicked="${(event: ImageHotspotTargetClickedEvent) => this.targetClicked(event.popupId)}"
            name="${target.brand_name}"
            image="${target.brand_image}"
            url="${target.brand_url}"
            x="${target.x}"
            y="${target.y}"
          ></csc-image-hotspot-popup>
        `)}

        ${this.tagTargets.map((target, index) => html`
          <csc-image-hotspot-popup
            popup-id="${`tag_${index}`}"
            ?active="${`tag_${index}` === this.activeTarget}"
            @image-hotspot-target-clicked="${(event: ImageHotspotTargetClickedEvent) => this.targetClicked(event.popupId)}"
            name="${target.tag_name}"
            url="${target.tag_url}"
            x="${target.x}"
            y="${target.y}"
          ></csc-image-hotspot-popup>
        `)}
        
        ${this.treeTargets.map((target, index) => html`
          <csc-image-hotspot-popup
            popup-id="${`tree_${index}`}"
            ?active="${`tree_${index}` === this.activeTarget}"
            @image-hotspot-target-clicked="${(event: ImageHotspotTargetClickedEvent) => this.targetClicked(event.popupId)}"
            name="${target.tree_name}"
            image="${target.tree_image}"
            url="${target.tree_url}"
            x="${target.x}"
            y="${target.y}"
          ></csc-image-hotspot-popup>
        `)}

        ${this.productTargets.map(target => html`
          <cse-hotspot-target
            class="target"
            style="top: ${target.y}%; left: ${target.x}%;"
            @click="${() => this.productTargetClicked(target.product_data_url)}"
          ></cse-hotspot-target>
        `)}

        ${this.productTargets.length > 0 ? html`
          <csc-image-hotspot-slide-in
            ?display-rating="${this.displayRating}"
            ?display-stock="${this.displayStock}"
            ?display-prices="${this.displayPrices}"
            locale="${this.locale}"
          ></csc-image-hotspot-slide-in>
        ` : nothing}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-image-hotspot': CSCImageHotspot,
  }
}
